import { useMemo } from "react";
import { resources } from "@constants/hardcoded-strings";
import { PolicyTypes } from "@enums/policy-types";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationPolicyConfig,
    FormattedRpServiceConfigurationPolicy,
    FormattedRpServiceConfigurationProperty
} from "@idw-types/data/rp-service-configuration";
import { AxnRpService } from "@idw-types/data/axn-rp-service";
import { AxnAttributeType, AxnAttributeTypeField, AxnCompany } from "@idw-types/data/reference-data/axn";
import { PolicyDetailErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { AlwaysObligatePolicyDetailsContainer } from "./always-obligate-policy-details-container/always-obligate-policy-details-container";
import { DecisionEnginePolicyEditorContainer } from "./decision-engine-policy-editor-container/decision-engine-policy-editor-container";
import { TrustScorePolicyDetailsContainer } from "./trust-score-policy-details-container/trust-score-policy-details-container";
import { AxnAcquiredAttribute } from "@idw-types/data/reference-data/axn/axn-acquired-attribute";
import { AvailableAssertionStatus } from "@idw-types/data/reference-data/generic";
import { AXNUIContent, MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { SimpleRPServiceWorkflow } from "@idw-types/data/reference-data/axn/simple-rp-service-workflow";

type PolicyDetailsContainerProps = {
    showTitle: boolean;
    isDisabled: boolean;
    isUsingThemedUI: boolean;
    validationErrors: PolicyDetailErrors;
    axnCompanies: Array<AxnCompany>;
    axnRpServices: Array<AxnRpService>;
    axnuiContent: AXNUIContent;
    selectedLanguage: LocaleTypes;
    availableLanguages: Array<LocaleTypes>;
    isMultiLanguageEnabled: boolean;
    multiLanguageAXNUIContent: MultiLanguageWrapper<AXNUIContent>;
    axnAttributeTypes: Array<AxnAttributeType>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    policyType: PolicyTypes;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    simpleRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    rpServiceID: string;
    workflowID: string;
    axnAcquiredAttributes: Array<AxnAcquiredAttribute>;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
};

export const PolicyDetailsContainer = (props: PolicyDetailsContainerProps) => {
    const {
        showTitle,
        isDisabled,
        isUsingThemedUI,
        validationErrors,
        axnCompanies,
        axnRpServices,
        selectedLanguage,
        axnuiContent,
        multiLanguageAXNUIContent,
        availableLanguages,
        isMultiLanguageEnabled,
        axnAttributeTypes,
        axnAttributeTypeFields,
        policyType,
        rpServiceConfigurationState,
        simpleRPServiceWorkflows,
        rpServiceID,
        workflowID,
        axnAcquiredAttributes,
        axnAvailableAssertionStatuses,
        setRpServiceConfigurationState
    } = props;

    const title = useMemo(() => {
        if (policyType === PolicyTypes.ALWAYS_OBLIGATE || policyType === PolicyTypes.TRUST_SCORE) {
            return resources.pages.verificationServiceDetails.tabs.policies.sections.details.title;
        } else if (policyType === PolicyTypes.DECISION_ENGINE) {
            return resources.pages.verificationServiceDetails.tabs.policies.sections.editor.title;
        }
        return null;
    }, [policyType]);

    const updatePolicies = (policies: Array<FormattedRpServiceConfigurationPolicy>) => {
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            policies
        });
    };

    const updatePolicyConfig = (policyConfig: FormattedRpServiceConfigurationPolicyConfig) => {
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            policyConfig
        });
    };
    const updateProperties= (properties: Array<FormattedRpServiceConfigurationProperty> ) => {
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties
        });
    };

    const updatePropertiesAndPolicyConfig = (properties: Array<FormattedRpServiceConfigurationProperty>, policyConfig: FormattedRpServiceConfigurationPolicyConfig) => {
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties,
            policyConfig
        });    
    }

    return (
        <div>
            {showTitle && (
                <ContentPageHeader title={title} />
            )}
            <ContentPageBody isMaxWidth={true}>
                {policyType === PolicyTypes.ALWAYS_OBLIGATE && (
                    <AlwaysObligatePolicyDetailsContainer
                        isDisabled={isDisabled}
                        validationErrors={validationErrors}
                        axnRpServices={axnRpServices}
                        policies={rpServiceConfigurationState.policies}
                        rpServiceID={rpServiceID}
                        workflowID={workflowID}
                        simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                        updatePolicies={updatePolicies}
                    />
                )}
                {policyType === PolicyTypes.DECISION_ENGINE && (
                    <DecisionEnginePolicyEditorContainer
                        isDisabled={isDisabled}
                        isUsingThemedUI={isUsingThemedUI}
                        axnCompanies={axnCompanies}
                        axnRpServices={axnRpServices}
                        axnAttributeTypes={axnAttributeTypes}
                        axnAttributeTypeFields={axnAttributeTypeFields}
                        selectedLanguage={selectedLanguage}
                        validationErrors={validationErrors}
                        axnuiContent={axnuiContent}
                        multiLanguageAXNUIContent={multiLanguageAXNUIContent}
                        availableLanguages={availableLanguages}
                        isMultiLanguageEnabled={isMultiLanguageEnabled}
                        properties={rpServiceConfigurationState.properties}
                        policyConfig={rpServiceConfigurationState.policyConfig}
                        attributes={rpServiceConfigurationState.attributes}
                        axnAcquiredAttributes={axnAcquiredAttributes}
                        serviceOfferings={rpServiceConfigurationState.serviceOfferings}
                        simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                        axnAvailableAssertionStatuses={axnAvailableAssertionStatuses}
                        rpServiceID={rpServiceID}
                        workflowID={workflowID}
                        updatePropertiesAndPolicyConfig={updatePropertiesAndPolicyConfig}
                        updateProperties={updateProperties}
                        updatePolicyConfig={updatePolicyConfig}
                    />
                )}
                {policyType === PolicyTypes.TRUST_SCORE && (
                    <TrustScorePolicyDetailsContainer
                        isDisabled={isDisabled}
                        validationErrors={validationErrors}
                        axnRpServices={axnRpServices}
                        policies={rpServiceConfigurationState.policies}
                        simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                        rpServiceID={rpServiceID}
                        workflowID={workflowID}
                        updatePolicies={updatePolicies}
                    />
                )}
            </ContentPageBody>
        </div>
    );
};